import getWindow from 'get-window';

import type { BlockRange } from '@gitbook/slate';
import { IS_IOS } from '@gitbook/slate-dev-environment';
import { isScrollableOverflowValue } from '@gitbook/util-dom';

import findSlateDOMNode from './find-dom-node';
import { isSelectionBackward } from './isSelectionBackward';

/*
 * Detect whether we are running IOS version 11
 */

const IS_IOS_11 = IS_IOS && Boolean(window.navigator.userAgent.match(/os 11_/i));

/*
 * Find the nearest parent with scrolling, or window.
 */
function findScrollContainer(el: Node, window: Window): HTMLElement {
    let parent = el.parentNode;
    let scroller;

    while (!scroller) {
        if (!parent?.parentNode) {
            break;
        }

        const style = window.getComputedStyle(parent as Element);
        const { overflowY } = style;

        if (isScrollableOverflowValue(overflowY)) {
            scroller = parent;
            break;
        }

        parent = parent.parentNode;
    }

    // COMPAT: Because Chrome does not allow doucment.body.scrollTop, we're
    // assuming that window.scrollTo() should be used if the scrollable element
    // turns out to be document.body or document.documentElement. This will work
    // unless body is intentionally set to scrollable by restricting its height
    // (e.g. height: 100vh).
    if (!scroller) {
        return window.document.body;
    }

    return scroller;
}

/*
 * Scroll the current block selection's focus point into view if needed.
 *
 * For BlockRange we don't have a native selection to work with
 * so we need to scroll to the block's DOM node.
 */
export function scrollToBlockRange(range: BlockRange) {
    const { focusBlock } = range;
    if (!focusBlock) {
        return;
    }
    scrollToSlateNode(focusBlock);
}

function scrollToSlateNode(nodeKey: string, scrollOptions?: ScrollIntoViewOptions) {
    const focusNode = findSlateDOMNode(nodeKey);
    if (!focusNode) {
        return;
    }

    const { bottom: frameBottom, top: frameTop } = getScrollFrameBounds(focusNode);
    const { bottom: targetBottom, top: targetTop } = focusNode.getBoundingClientRect();

    // we only want to scroll into view if the element is completely out of the viewport.
    if (targetTop > frameBottom || targetBottom < frameTop) {
        focusNode.scrollIntoView(
            scrollOptions ?? {
                block: 'nearest',
            }
        );
    }
}

/*
 * Scroll the current selection's focus point into view if needed.
 */
function scrollToSelection(selection: Selection) {
    if (IS_IOS_11) {
        return;
    }
    if (!selection.focusNode) {
        return;
    }
    if (selection.rangeCount < 1) {
        return;
    }

    // Slate has patched scrolling to an expanded selection as this
    // was affecting the selection of multiple blocks and dragging behavior:
    // https://github.com/ianstormtaylor/slate/blob/main/packages/slate-react/src/components/editable.tsx#L1693
    if (!selection.isCollapsed) {
        return;
    }

    const element: Element | null = isElement(selection.focusNode)
        ? selection.focusNode
        : selection.focusNode.parentElement;

    // A void's spacer is pinned to the top of the node, so its caret says nothing about what is
    // visible: only scroll when the whole node is out of view.
    const voidElement = element?.closest('[data-slate-spacer]')?.parentElement;
    if (voidElement) {
        scrollToPosition({ rect: voidElement.getBoundingClientRect(), element: voidElement });
        return;
    }

    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(isSelectionBackward(selection));
    const cursorRect = range.getBoundingClientRect();

    if (element) {
        scrollToPosition({ rect: cursorRect, element });
    }
}

function isElement(node: Node): node is Element {
    return typeof node === 'object' && node != null && node.nodeType === 1;
}

interface ScrollToPositonProps {
    rect: DOMRect;
    element: Element;
}

/*
 * Update the scroll position of the scrollable container
 */
function scrollToPosition({ rect, element }: ScrollToPositonProps) {
    const {
        top: frameTop,
        bottom: frameBottom,
        left: frameLeft,
        right: frameRight,
    } = getScrollFrameBounds(element);
    if (
        rect.top > frameBottom ||
        rect.bottom < frameTop ||
        rect.left > frameRight ||
        rect.right < frameLeft
    ) {
        element.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
        });
    }
}

// a small threshold at bottom and top of scroll frame where we still
// trigger scrolling even if the node is partially visible.
const DEFAULT_SCROLL_THRESHOLD = 56;

/**
 * Get the bounds (top, bottom, left, right) and the scrollX and scrollY position
 * of a node's closest scrollable frame
 */
function getScrollFrameBounds(node: Node) {
    const window = getWindow(node);
    const scroller = findScrollContainer(node, window);
    const isWindow =
        scroller === window.document.body || scroller === window.document.documentElement;

    if (isWindow) {
        const { innerWidth, innerHeight, scrollY, scrollX } = window;
        const top = DEFAULT_SCROLL_THRESHOLD;
        const height = innerHeight - 2 * DEFAULT_SCROLL_THRESHOLD;

        const left = 0;
        const width = innerWidth;
        return {
            element: window,
            scrollY,
            scrollX,
            top,
            bottom: top + height,
            left,
            right: left + width,
        };
    } else {
        const { offsetWidth, offsetHeight, scrollTop, scrollLeft } = scroller;
        const { borderTopWidth, borderBottomWidth, borderLeftWidth, borderRightWidth } =
            window.getComputedStyle(scroller);

        const scrollerRect = scroller.getBoundingClientRect();

        const top =
            scrollerRect.top + Number.parseInt(borderTopWidth, 10) + DEFAULT_SCROLL_THRESHOLD;
        const height =
            offsetHeight -
            Number.parseInt(borderTopWidth, 10) -
            Number.parseInt(borderBottomWidth, 10) -
            2 * DEFAULT_SCROLL_THRESHOLD;

        const left = scrollerRect.left + Number.parseInt(borderLeftWidth, 10);
        const width =
            offsetWidth -
            Number.parseInt(borderLeftWidth, 10) -
            Number.parseInt(borderRightWidth, 10);

        return {
            element: scroller,
            scrollX: scrollLeft,
            scrollY: scrollTop,
            left,
            right: left + width,
            top,
            bottom: top + height,
        };
    }
}

export default scrollToSelection;
